import { useMemo } from 'react';
import { Dropdown } from '@Components/Dropdown';
import {
  FieldValue,
  OverviewFieldsRenderer,
  OverviewSection
} from '@Components/EntityOverviewForm';
import {
  IO_TEXT_SECTIONS,
  OVERVIEW_SECTIONS,
  StreamingNetworkRecord
} from './streaming-networks-constants';
import { PlatformSelect } from './PlatformSelect';

interface NetworkGeneralTabProps {
  network: StreamingNetworkRecord;
  editing: boolean;
  dspOptions: string[];
  platformOptions: string[];
  networkGroups: string[];
  onFieldChange: <K extends keyof StreamingNetworkRecord>(
    field: K,
    value: StreamingNetworkRecord[K]
  ) => void;
}

export const NetworkGeneralTab = ({
  network,
  editing,
  dspOptions,
  platformOptions,
  networkGroups,
  onFieldChange
}: NetworkGeneralTabProps) => {
  // OverviewFieldsRenderer is a config-driven form that reads/writes fields by name, so it hands
  // back a plain string key + value — this bridges that back to the record's typed setter.
  const handleFieldChange = (field: string, value: FieldValue) =>
    onFieldChange(field as keyof StreamingNetworkRecord, value as never);

  const source = network as unknown as Record<string, FieldValue>;

  // See OVERVIEW_SECTIONS' comment on 'Network Group' for why this field's options are filled in
  // here instead of the shared static config.
  const sections = useMemo<OverviewSection[]>(
    () =>
      OVERVIEW_SECTIONS.map(section => ({
        ...section,
        fields: section.fields.map(field =>
          field.field === 'networkGroup'
            ? { ...field, options: networkGroups.map(group => ({ label: group, value: group })) }
            : field
        )
      })),
    [networkGroups]
  );

  return (
    <div className='sn-general'>
      <OverviewFieldsRenderer
        sections={sections}
        source={source}
        editing={editing}
        onFieldChange={handleFieldChange}
      />
      <div className='eof-fields'>
        <div className='eof-section'>
          <div className='eof-section-title'>Platforms</div>
          <PlatformSelect
            platforms={network.platforms}
            options={platformOptions}
            editing={editing}
            onChange={platforms => onFieldChange('platforms', platforms)}
          />
        </div>
        <div className='eof-section'>
          <div className='eof-section-title'>DSPs</div>
          <div className='sn-dsp-dropdown'>
            <Dropdown<string>
              searchable
              creatable
              multiSelect
              showChips
              options={dspOptions.map(dsp => ({ label: dsp, value: dsp }))}
              value={network.dsp}
              placeholder={editing ? 'Add a DSP…' : 'None'}
              disabled={!editing}
              onChange={dsp => onFieldChange('dsp', dsp)}
            />
          </div>
        </div>
      </div>
      <OverviewFieldsRenderer
        sections={IO_TEXT_SECTIONS}
        source={source}
        editing={editing}
        onFieldChange={handleFieldChange}
      />
    </div>
  );
};
